<template>
    <div id="root">
        <router-view id="router-view" />
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import cleverbridge from '@core/mixins/cleverbridge.js';
import utm from '@core/mixins/utm.js';
import type { SSRContext } from '@model/ssr/context';
import 'the-new-css-reset/css/reset.css'; // eslint-disable-line import/no-extraneous-dependencies
import '@core/styles/vars.pcss';
import '@core/styles/defaults.pcss';
import '@core/styles/vars/grid-system.scss';
import '@core/styles/spacings.scss';
import '@core/styles/cols.scss';
import '@core/styles/custom-fonts.scss';
import '@core/styles/bundle-corp-uikit.scss';
// NB: font stylesheet is preload + async import in root.html for better UX

declare global {
    interface Window {
        google: any;
        markerClusterer: any;
        dataLayer: any;
        splitsCurrentActiveSet: any;
        ga: any;
        getDataFromPocket: (key: string, throwIfEmpty?: boolean) => any;
        VWO_OUTCOME: 'pending' | 'regular' | 'redirect',
    }
}

export default Vue.extend({
    name: 'AppRoot',

    mixins: [utm, cleverbridge],

    computed: {
        page() {
            return this.$store.state.pages?.page || {};
        },
    },

    mounted() {
        this.pushGaEvents();
        this.handleClick();
        this.handleURLHash();
        this.storeLocalUTM();
        this.storeSessionUTM();
    },

    beforeMount() {
        window.addEventListener('load', this.addLabelUTM);
    },

    beforeDestroy() {
        window.removeEventListener('load', this.addLabelUTM);
    },

    methods: {
        async deleteSensitiveQueryParams() {
            // we clear potential first_name and last_name params to prevent them from being sent to facebook
            if (Object.keys(this.$route?.query).length) {
                this.$store.commit('trial/SET_QUERY_STRING_DATA', this.$route.query);

                const query = { ...this.$route.query };
                const expectedKeys = [
                    'osauid',
                    'aan',
                    'company',
                    'email',
                    'first_name',
                    'last_name',
                    'phone',
                    'country',
                    'currency',
                    'price',
                    'opportunity_id',
                    'tenantId',
                ];

                expectedKeys.forEach((key) => (delete query[key]));

                if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
                    await this.$router.replace({ query });
                }
            }
        },

        async pushGaEvents() {
            // TODO: this function does too many unrelated things
            if (this.$store.state.pages && !this.page?.settings?.redirectPer6sense?.length) {
                this.$store.dispatch('pages/setLoading', false);
            }

            await this.deleteSensitiveQueryParams();

            window.dataLayer = window.dataLayer || [];

            this.initAnonymousGATrackerDelayed(new Date());

            const customPageEvents = this.$store.state.pages?.page?.settings?.pushEventToDataLayer || [];
            customPageEvents.forEach((item) => window.dataLayer.push(item));
        },

        addLabelUTM() {
            if (!this.getSessionUTM.length) {
                return;
            }
            Array.from(document.getElementsByTagName('a')).forEach((element: HTMLAnchorElement): void => {
                const url = this.setQueryString(element.href);
                if (url) element.setAttribute('href', url);
            });
        },

        handleClick(): void {
            document.body.addEventListener('click', (e: any) => {
                // WEB-42890: Support dynamically injected surl links from the api content
                if (e.target.dataset?.surl) {
                    e.preventDefault();
                    const surl = JSON.parse(e.target.dataset.surl);
                    const options = this.storeOptionsSURL(surl);
                    this.redirectToCleverbridge(options);
                    return;
                }
                if (e.target.dataset?.event) {
                    window.dataLayer.push({
                        eventLabel: e.target.href,
                        ...e.target.dataset,
                    });
                }
            });
        },

        handleURLHash(): void {
            const hash = this.$route.hash.replace('/', ''); // TODO: why replace?
            if (!hash) {
                return;
            }

            if (window.location.hash !== hash) {
                window.location.hash = hash;
            }

            document.addEventListener('readystatechange', () => this.scrollToSlice(hash));
        },

        scrollToSlice(hash: string): void {
            if (document.readyState !== 'complete') {
                return;
            }

            // hash can contain virtually anything so querySelector can throw an invalid selector error
            let el = null;
            try {
                el = document.querySelector(hash);
            } catch (e) {
                // NO OP
            }

            if (!el) {
                return;
            }

            const scrollMargin = Array.from(document.querySelectorAll('.directive-v-sticky'))
                .map((sticky: HTMLElement) => sticky.offsetHeight || 0)
                .reduce((acc: number, next: number) => acc + next, 0);

            window.scroll({
                top: el.getBoundingClientRect().top + window.scrollY - scrollMargin,
                behavior: 'smooth',
            });
        },

        storeLocalUTM(): void {
            if (!window.localStorage) {
                return;
            }

            const utmContent = new URL(window.location.href).searchParams.get('utm_content');
            const allowedKeys = new Set(['smart', 'Smart', 'SmartDisplay']);

            if (allowedKeys.has(utmContent)) {
                localStorage.setItem('utm_content', utmContent);
            }
        },

        storeSessionUTM(): void {
            if (!window.sessionStorage) {
                return;
            }

            const searchParams = new URL(window.location.href).searchParams;

            const labels = [
                'utm_campaign',
                'utm_content',
                'utm_medium',
                'utm_source',
                'utm_term',
                'x-audience',
                'x-opp-id',
                'x-tenant_id',
                'x-wave',
            ];

            labels
                .map((key) => ({ key, val: searchParams.get(key) }))
                .filter((pair) => Boolean(pair.val))
                .forEach((x) => sessionStorage.setItem(x.key, x.val));
        },

        initAnonymousGATrackerDelayed(startedAt: any): void {
            // Universal Google Analytics now require a tracker name for its calls: `ga("gtm5.send", ...)`
            // For some 3rd party libs (Onetrust, maybe more)
            // it's necessary to have an unnamed tracker: `ga("send", ...)`
            // As GTM doesn't fire a `ga_loaded` event of any kind, we must check it in a timer callback

            const MAX_DETECT_TIME = 10000; // ms
            const DETECT_INTERVAL = 100; // ms
            const CURRENT:any = new Date();

            if (window.ga && window.ga.loaded && window.ga.getAll()[0]) {
                const defaultTracker = window.ga.getAll()[0];
                const opts = {
                    cookieDomain: 'auto',
                    allowLinker: true,
                };
                window.ga('create', defaultTracker.get('trackingId'), opts);
            } else if (CURRENT - startedAt > MAX_DETECT_TIME) {
                // It took too long, we just give up
                // return;
            } else {
                setTimeout(() => this.initAnonymousGATrackerDelayed(startedAt), DETECT_INTERVAL);
            }
        },
    },

    async serverPrefetch(): Promise<void> {
        const ctx = this.$ssrContext as SSRContext;

        ctx.res.pocket.splits = await ctx.Synced.slicesData.query('splits', 'null') ?? {};
        ctx.res.pocket.matomo = { baseUrl: ctx.env.HEAD_SITE_MAIN_PUBLIC_BASE_URL_MATOMO };
        ctx.res.pocket.clientinfo = { ipSource: '%%CLIENT_IP_SOURCE%%' }; // placeholder to rewrite on LB
    },
});
</script>
